import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getSettings, getUserProfile } from '../utils/storage';
import { redirectToPortal, getPortalUrl } from '../services/stripeService';
import PricingModal from './PricingModal';

/**
 * Account management page — opened in the native browser (Safari/Chrome)
 * when native app users tap "Manage Plan & Account Settings".
 *
 * Shows subscription info, Stripe billing portal access, and profile details.
 * No bottom nav, no onboarding — focused billing/account management.
 */
export default function Account({ onToast }) {
  const { user, signOut } = useAuth();
  const [billingLoading, setBillingLoading] = useState(false);
  const [showPricing, setShowPricing] = useState(false);

  const settings = getSettings();
  const userProfile = getUserProfile();
  const { subscription } = settings;

  const displayName = userProfile
    ? `${userProfile.firstName} ${userProfile.lastName}`
    : settings.profile?.name || 'User';
  const displayEmail = userProfile?.email || user?.email || settings.profile?.email || '';

  const usagePercent = subscription.creditsTotal > 0
    ? Math.round((subscription.creditsUsed / subscription.creditsTotal) * 100)
    : 0;

  const handleManageBilling = async () => {
    setBillingLoading(true);
    try {
      await redirectToPortal(user?.email, user?.id);
    } catch (err) {
      if (import.meta.env.DEV) console.error('[Account] Portal error:', err);
      if (err?.message?.includes('No Stripe customer')) {
        onToast('Your subscription was created through the App Store. To change your subscription, please use the mobile app.');
      } else {
        const portalUrl = getPortalUrl();
        if (portalUrl) {
          window.open(portalUrl, '_blank');
        } else {
          onToast(err.message || 'Unable to open billing portal');
        }
      }
    } finally {
      setBillingLoading(false);
    }
  };

  return (
    <div className="screen" style={{ paddingBottom: 40, overflowY: 'auto' }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 24, paddingTop: 8 }}>
        <img
          src="/logo-tall.png"
          alt="Proper Miles"
          style={{ height: 48, objectFit: 'contain', display: 'block', margin: '0 auto 12px' }}
        />
        <h1 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-primary)' }}>
          Account & Billing
        </h1>
      </div>

      {/* Profile */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            background: 'var(--accent)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: '1.125rem',
            color: 'white',
            flexShrink: 0,
          }}>
            {displayName.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: '1rem' }}>{displayName}</div>
            <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>{displayEmail}</div>
          </div>
        </div>
      </div>

      {/* Subscription */}
      <div className="card" style={{ marginBottom: 12 }}>
        <h3 style={sectionTitleStyle}>Subscription</h3>
        <div style={{
          padding: '14px 16px',
          borderRadius: 'var(--radius-sm)',
          border: '1.5px solid var(--accent)',
          background: 'var(--accent-subtle)',
          marginBottom: 12,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ fontWeight: 700, fontSize: '0.9375rem', textTransform: 'capitalize' }}>
              {subscription.plan}
            </span>
            <span style={{
              fontSize: '0.5625rem', fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: '0.05em', padding: '2px 7px', borderRadius: 4,
              background: 'var(--accent)', color: '#fff',
            }}>
              CURRENT PLAN
            </span>
          </div>
          <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
            {subscription.plan === 'free'
              ? '1 vehicle \u00b7 1 garage \u00b7 15 AI credits/mo'
              : subscription.plan === 'enthusiast'
                ? '5 vehicles \u00b7 1 garage \u00b7 100 AI credits/mo'
                : subscription.plan === 'collector'
                  ? '15 vehicles \u00b7 3 garages \u00b7 250 AI credits/mo'
                  : subscription.plan === 'showroom'
                    ? '50 vehicles \u00b7 5 garages \u00b7 500 AI credits/mo'
                    : 'Unlimited vehicles \u00b7 Unlimited garages \u00b7 500 AI credits/mo'}
          </div>
        </div>

        {subscription.plan === 'free' ? (
          <button
            className="btn btn-primary"
            onClick={() => setShowPricing(true)}
            style={{ fontSize: '0.8125rem', padding: '10px 16px' }}
          >
            Upgrade your plan
          </button>
        ) : (
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn btn-secondary"
              onClick={() => setShowPricing(true)}
              style={{ flex: 1, fontSize: '0.8125rem', padding: '10px 16px' }}
            >
              Change Plan
            </button>
            <button
              className="btn btn-secondary"
              disabled={billingLoading}
              onClick={handleManageBilling}
              style={{ flex: 1, fontSize: '0.8125rem', padding: '10px 16px', opacity: billingLoading ? 0.6 : 1 }}
            >
              {billingLoading ? 'Loading...' : 'Manage Billing'}
            </button>
          </div>
        )}
      </div>

      {/* Research Credits */}
      <div className="card" style={{ marginBottom: 12 }}>
        <h3 style={sectionTitleStyle}>Research Credits</h3>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
            {subscription.creditsUsed} of {subscription.creditsTotal} used this month
          </span>
          <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-primary)' }}>
            {subscription.creditsTotal - subscription.creditsUsed} remaining
          </span>
        </div>
        <div style={{
          width: '100%',
          height: 8,
          borderRadius: 4,
          background: 'var(--bg-elevated)',
          overflow: 'hidden',
        }}>
          <div style={{
            width: `${usagePercent}%`,
            height: '100%',
            borderRadius: 4,
            background: usagePercent > 80 ? 'var(--warning)' : 'var(--accent)',
            transition: 'width 0.3s ease',
          }} />
        </div>
        <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 6 }}>
          Resets monthly. Upgrade your plan for more credits.
        </p>
      </div>

      {/* Sign Out */}
      <button
        className="btn btn-secondary"
        onClick={async () => {
          try {
            await signOut();
          } catch {
            onToast('Sign out failed');
          }
        }}
        style={{ marginBottom: 16, width: '100%', padding: '12px 16px' }}
      >
        Sign Out
      </button>

      {/* Return-to-app note */}
      <p style={{
        textAlign: 'center',
        fontSize: '0.8125rem',
        color: 'var(--text-muted)',
        lineHeight: 1.5,
        marginTop: 8,
      }}>
        You can close this tab and return to the app.
      </p>

      {/* Pricing Modal */}
      {showPricing && (
        <PricingModal onClose={() => setShowPricing(false)} onToast={onToast} />
      )}
    </div>
  );
}

const sectionTitleStyle = {
  fontSize: '0.75rem',
  fontWeight: 600,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: 12,
};
